import {
	useDeleteDailyMaxDepositMutation,
	useGetDailyMaxDepositQuery,
	useSetDailyMaxDepositMutation,
} from "@api/accountManagement";
import { Form } from "antd";
import { useMemo, useState } from "react";

const AMOUNT_REGEX = /^[0-9]+(\.[0-9]{1,2})?$/;

const useController = ({ showAlert }) => {
	const [form] = Form.useForm();
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	const { data: depositLimitData } = useGetDailyMaxDepositQuery();

	const [setDailyMaxDeposit, { isLoading: isSetting }] = useSetDailyMaxDepositMutation();
	const [deleteDailyMaxDeposit, { isLoading: isDeleting }] = useDeleteDailyMaxDepositMutation();

	const state = useMemo(() => {
		const amount = depositLimitData?.daily_max_deposit || 0;
		const startDate = depositLimitData?.createdAt || 0;
		const isSet = amount > 0;

		return {
			isDepositLimitSet: isSet,
			depositLimitAmount: amount,
			depositLimitStartDate: startDate,
			isSetting,
			isDeleting,
			isButtonDisabled,
		};
	}, [depositLimitData, isSetting, isDeleting, isButtonDisabled]);

	const handlers = useMemo(
		() => ({
			handleDepositLimit: async (values) => {
				try {
					await setDailyMaxDeposit({
						maxDailyDeposit: parseFloat(values.depositLimitAmount),
					}).unwrap();

					showAlert({
						type: "success",
						msg: "DAILY_DEPOSIT_LIMIT_SET_SUCCESS",
						title: "SUCCESS",
					});
					form.resetFields();
					setIsButtonDisabled(true);
				} catch (error) {
					const errorMessage =
						error?.data?.message || error?.message || "DAILY_DEPOSIT_LIMIT_SET_ERROR";
					showAlert({
						type: "error",
						msg: errorMessage,
						title: "ERROR",
						noTranslate: true,
					});
					console.error("Failed to set deposit limit:", error);
				}
			},

			handleFormValuesChange: (changedValues) => {
				setIsButtonDisabled(!changedValues.depositLimitAmount);
			},

			handleDeleteDepositLimit: async () => {
				try {
					await deleteDailyMaxDeposit().unwrap();
					showAlert({
						type: "success",
						msg: "DAILY_DEPOSIT_LIMIT_REMOVE_SUCCESS",
						title: "SUCCESS",
					});
				} catch (error) {
					const errorMessage =
						error?.data?.message ||
						error?.message ||
						"DAILY_DEPOSIT_LIMIT_REMOVE_ERROR";
					showAlert({
						type: "error",
						msg: errorMessage,
						title: "ERROR",
						noTranslate: true,
					});
					console.error("Failed to remove deposit limit:", error);
				}
			},
		}),
		[setDailyMaxDeposit, deleteDailyMaxDeposit, form, showAlert]
	);

	// Helpers
	const helpers = useMemo(
		() => ({
			AMOUNT_REGEX,
			form,
		}),
		[form]
	);

	return {
		state,
		handlers,
		helpers,
	};
};

export default useController;
